
import Vue from 'vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import _slice from 'lodash/slice';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { functions } from '@/firebase';
import DocumentReadyDialog from "@/components/DocumentReadyDialog.vue";
import { Team } from '@run-diffusion/shared';
import { TeamUserMixin } from '@/mixins';

// Kept in sync with MAX_RECEIPTS_PER_ZIP in the getStripePaymentReceiptsZipUrl function, so
// "download this page" is always exactly one zip request. It is also Stripe's ceiling on
// paymentIntents.search, so a page can never hold more than this.
const BULK_RECEIPTS_PAGE_SIZE: number = 100;

// The callable SDK gives every request 70s by default. Both of these do far more work than
// that at a page size of 100, so each gets the same ceiling as the function behind it.
const PAYMENTS_PAGE_TIMEOUT_MS: number = 240000;
const BULK_RECEIPTS_TIMEOUT_MS: number = 540000;

// A wallet has no card number to end in, so its row names the wallet instead
const PAYMENT_METHOD_WALLET_NAMES: Record<string, string> = {
	cashapp: 'Cash App Pay',
	alipay: 'Alipay',
	link: 'Link',
};

export default Vue.extend({
	name: 'PaymentsHistoryDialog',
	mixins: [TeamUserMixin],
	props: {
		value: { type: Boolean, default: false },
	},
	data () {
		return {
			historyTypeValue: 0,
			PRIVATE_HISTORY_TYPE: 0,
			TEAM_HISTORY_TYPE: 1,

			currPage: 0,
			pageSize: BULK_RECEIPTS_PAGE_SIZE,
			currPayments: [],
			payments: [],
			lastPaymentIntentsResponse: null,
			loadingInitialPayments: false,
			loadingMorePayments: false,

			paymentReceiptUrlToOpen: null,
			paymentReceiptUrlIsZip: false,
			loadingReceiptUrlForPaymentId: null,
			loadingReceiptsZip: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		canGoPrevPage () {
			return this.currPage > 0;
		},
		canGoNextPage () {
			return (
				(this.lastPaymentIntentsResponse && this.lastPaymentIntentsResponse.has_more) ||
				this.currPage < Math.floor(this.payments.length / this.pageSize)
			);
		},
		bulkDownloadSubtitle () {
			const count: number = this.currPayments.length;
			const page: number = this.currPage + 1;
			return count === 1
				? `The single invoice on page ${page}, as a PDF`
				: `All ${count} invoices on page ${page}, each as its own PDF`;
		},
		bulkDownloadButtonLabel () {
			return this.currPayments.length === 1
				? 'Download invoice'
				: `Download all ${this.currPayments.length}`;
		},
		bulkDownloadProgressText () {
			const count: number = this.currPayments.length;
			return count === 1
				? 'Generating the invoice PDF and packaging it into a zip file. This can take a moment, so keep this window open.'
				: `Generating ${count} invoice PDFs and packaging them into a zip file. This can take a couple of minutes, so keep this window open.`;
		},
		isBulkDownloadDisabled () {
			return (
				this.loadingReceiptsZip ||
				this.loadingMorePayments ||
				!!this.loadingReceiptUrlForPaymentId ||
				!this.currPayments.length
			);
		},
		receiptReadyDialogProps () {
			if (!this.paymentReceiptUrlIsZip) return {};
			return {
				title: 'Invoices Ready',
				icon: 'mdi-folder-zip-outline',
				message: 'Download the zip file to get every invoice on this page as its own PDF',
				confirmText: 'Download Zip',
			};
		},
	},
	created () {
		this.loadInitialPayments(this.value, this.historyTypeValue);
	},
	watch: {
		value (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.loadInitialPayments(newVal, this.historyTypeValue);
			}
		},
		historyTypeValue (newVal, oldVal) {
			if (newVal !== oldVal) {
				this.loadInitialPayments(this.value, newVal);
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'goToStripeSubscriptionPortal',
		]),
		onGoToBillingPortalClick () {
			this.goToStripeSubscriptionPortal(this.getIsTeamHistory(this.team, this.historyTypeValue) ? this.team : null);
		},
		getIsTeamHistory (team: Team, historyTypeValue: number) {
			return !!(team && historyTypeValue === this.TEAM_HISTORY_TYPE);
		},
		onClose () {
			this.$emit('on-close');
		},
		onReceiptReadyDialogCancel () {
			this.paymentReceiptUrlToOpen = null;
			this.paymentReceiptUrlIsZip = false;
		},
		onOpenReceiptUrlClick () {
			if (this.paymentReceiptUrlToOpen) {
				window.open(this.paymentReceiptUrlToOpen, '_blank');
			}
			setTimeout(() => {
				this.paymentReceiptUrlToOpen = null;
				this.paymentReceiptUrlIsZip = false;
			}, 300);
		},
		/*
		The row's headline. A payment's own description only leads when the line under it says something
		else, its line items or its invoice's. Otherwise that line already shows the description, as it
		does for an auto top-up, and "$11.91 Tokens auto top-up" sat over "Tokens auto top-up".
		 */
		getPaymentTitle (payment: any): string {
			const description: string = this._get(payment, 'firebaseObj.description') || '';
			const subtitleShowsItems: boolean = !!(this._get(payment, 'firebaseObj.items.length') || this._get(payment, 'invoice.lines.data.length'));
			return description && subtitleShowsItems ? description : 'Payment';
		},
		getPaymentMethodDetailsText (payment: any) {
			if (this._get(payment, 'invoice')) {
				return '(from invoice)';
			}
			/*
			How it was paid lives on the charge. Payments synced long ago carry their charges inline; newer
			ones, auto top-ups among them, only name the charge, which getStripePaymentIntents expands.
			 */
			const details: any = this._get(payment, 'paymentIntent.latest_charge.payment_method_details') ||
				this._get(payment, 'firebaseObj.charges.data[0].payment_method_details') ||
				null;
			const last4: string = this._get(details, 'card.last4');
			if (last4) return `(payment method ending in ${last4})`;
			const walletName: string = PAYMENT_METHOD_WALLET_NAMES[this._get(details, 'type')];
			if (walletName) return `(paid with ${walletName})`;
			return null;
		},
		// getNewTabPaymentReceiptUrl (payment: any) {
		// 	const successfulCharge: any = _find(this._get(payment, 'firebaseObj.charges.data', []), ['status', 'succeeded']);
		// 	return successfulCharge ? successfulCharge.receipt_url : null;
		// },
		async onDownloadPaymentReceipt (paymentId: string) {
			this.loadingReceiptUrlForPaymentId = paymentId;
			const url: string = await this.getStripePaymentReceiptUrl(paymentId);
			if (url) {
				this.paymentReceiptUrlIsZip = false;
				this.paymentReceiptUrlToOpen = url;
			}
			this.loadingReceiptUrlForPaymentId = null;
		},
		async onDownloadPageReceipts () {
			const paymentIds: string[] = (this.currPayments || [])
				.map((payment: any) => this._get(payment, 'paymentIntent.id'))
				.filter((paymentId: string) => !!paymentId);
			if (!paymentIds.length) return;

			this.loadingReceiptsZip = true;
			const { url, skippedCount } = await this.getStripePaymentReceiptsZipUrl(paymentIds);
			this.loadingReceiptsZip = false;

			if (url) {
				if (skippedCount) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.WARN,
						message: `${skippedCount} of ${paymentIds.length} invoices could not be generated and were left out of the zip file`,
						show: true,
					});
				}
				this.paymentReceiptUrlIsZip = true;
				this.paymentReceiptUrlToOpen = url;
			}
		},
		sliceCurrPayments () {
			const startIndex: number = (this.currPage * this.pageSize);
			const endIndex: number = ((this.currPage + 1) * this.pageSize);
			this.currPayments = _slice(
				this.payments,
				startIndex,
				endIndex,
			);
		},
		onPrevPage () {
			this.currPage = this.currPage - 1;
			if (this.currPage < 0) this.currPage = 0;

			this.sliceCurrPayments();
		},
		async onNextPage () {
			this.currPage = this.currPage + 1;

			if (this.lastPaymentIntentsResponse && this.lastPaymentIntentsResponse.has_more) {
				const endIndex: number = (this.currPage * this.pageSize);
				if (endIndex >= this.payments.length) {
					await this.loadMorePayments();
				} else {
					this.sliceCurrPayments();
				}
			} else {
				this.sliceCurrPayments();
			}
		},
		async getStripePaymentReceiptUrl (paymentId: string) {
			const onError: Function = (e) => {
				console.error('Error loading Stripe payment receipt url: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading receipt',
					show: true,
				});
			};

			try {
				const functionRef = functions
					.httpsCallable('getStripePaymentReceiptUrl');
				const { url } = (await functionRef({
					paymentId,
					...(this.getIsTeamHistory(this.team, this.historyTypeValue) && {
						teamId: this.team.id,
					}),
				})).data;
				if (!url) {
					onError(new Error('getStripePaymentReceiptUrl returned undefined url'));
				}

				return url;
			} catch (e) {
				onError(e);
				return null;
			}
		},
		async getStripePaymentReceiptsZipUrl (paymentIds: string[]) {
			const onError: Function = (e) => {
				console.error('Error loading Stripe payment receipts zip url: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error generating invoices',
					show: true,
				});
			};

			try {
				const functionRef = functions
					.httpsCallable('getStripePaymentReceiptsZipUrl', { timeout: BULK_RECEIPTS_TIMEOUT_MS });
				const { url, skippedCount, message } = (await functionRef({
					paymentIds,
					...(this.getIsTeamHistory(this.team, this.historyTypeValue) && {
						teamId: this.team.id,
					}),
				})).data;
				if (!url) {
					onError(new Error(message || 'getStripePaymentReceiptsZipUrl returned undefined url'));
					return { url: null, skippedCount: 0 };
				}

				return { url, skippedCount: skippedCount || 0 };
			} catch (e) {
				onError(e);
				return { url: null, skippedCount: 0 };
			}
		},
		async loadMoreStripePaymentIntents (nextPage: number, historyTypeValue: number) {
			const onError: Function = (e) => {
				console.error('Error loading Stripe payment intents: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading payments',
					show: true,
				});
			};

			try {
				const functionRef = functions
					.httpsCallable('getStripePaymentIntents', { timeout: PAYMENTS_PAGE_TIMEOUT_MS });
				const { response } = (await functionRef({
					nextPage,
					pageSize: this.pageSize,
					...(this.getIsTeamHistory(this.team, historyTypeValue) && {
						teamId: this.team.id,
					}),
				})).data;
				if (!response) {
					onError(new Error('getStripePaymentIntents returned undefined response'));
					return { success: false, response: null };
				}

				return {
					success: true,
					response,
				};
			} catch (e) {
				onError(e);
				return {
					success: false,
					response: null,
				};
			}
		},
		async loadInitialPayments (dialogOpen: boolean, historyTypeValue: number) {
			if (!this.loadingInitialPayments && !this.loadingMorePayments && dialogOpen) {
				this.loadingInitialPayments = true;
				this.lastPaymentIntentsResponse = null;
				const { success, response } = await this.loadMoreStripePaymentIntents(null, historyTypeValue);
				this.loadingInitialPayments = false;
				if (success) {
					this.currPage = 0;
					this.lastPaymentIntentsResponse = response;
					this.currPayments = this._get(response, 'data') || [];
					this.payments = this.currPayments;
				}
			}
		},
		async loadMorePayments () {
			if (!this.loadingInitialPayments && !this.loadingMorePayments) {
				this.loadingMorePayments = true;
				const { success, response } = await this.loadMoreStripePaymentIntents(this._get(this.lastPaymentIntentsResponse, 'next_page') || null, this.historyTypeValue);
				this.loadingMorePayments = false;
				if (success) {
					this.lastPaymentIntentsResponse = response;
					this.currPayments = this._get(response, 'data') || [];
					this.payments = this.payments.concat(this.currPayments);
					this.$scrollTo('#payments-history-dialog-scroll-anchor', 500, {
						container: '#payments-history-dialog-scroll-container',
					});
				}
			}
		},
	},
	components: {
		DocumentReadyDialog,
		LoadingSVG,
		EmptyState,
	},
});
